import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './item.css';
import { Star, Truck, Heart, ShoppingCart, Check } from 'lucide-react';
import { formatPrice, calculateDiscount } from '../../utils/helpers';
import { useWishlist } from '../../hooks/useWishlist';
import { useCart } from '../../hooks/useCart';

const Item = ({ 
  id, 
  image, 
  name, 
  newPrice, 
  oldPrice,
  fromPrice = false,
  sold = 0, 
  rating = 4.5, 
  shipping = 'Free shipping', 
  hasGreenBg = false,
  inStock = true,
  colors = []
}) => {
  const { t } = useTranslation('common');
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  
  const discountPercent = calculateDiscount(oldPrice, newPrice);
  
  // Wishlist functionality
  const { isInWishlist, toggleWishlist, isToggling } = useWishlist();
  const isWishlisted = isInWishlist(id);
  
  // Cart functionality
  const { addToCart, isAddingToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart({ productId: id, quantity: 1 }, {
      onSuccess: () => {
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 2000);
      }
    });
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleWishlist(id);
    } catch (error) {
      // Show user-friendly error message
      alert(error.message || 'Failed to update wishlist');
    }
  };

  return (
    <div 
      className={`product-card ${hasGreenBg ? 'green-bg' : ''} ${!inStock ? 'out-of-stock' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/product/${id}`} className="product-link" onClick={() => window.scrollTo(0, 0)}>
        <div className="product-image-container">
          <div className="product-badges">
            {discountPercent > 0 && (
              <span className="badge discount-badge">-{discountPercent}%</span>
            )}
            {!inStock && (
              <span className="badge out-of-stock-badge">{t('outOfStock', { defaultValue: 'Out of Stock' })}</span>
            )}
            {sold > 100 && inStock && (
              <span className="badge hot-badge">🔥 {t('hot', { defaultValue: 'Hot' })}</span>
            )}
          </div>

          <button
            type="button"
            className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
            onClick={handleToggleWishlist}
            disabled={isToggling}
            aria-label={isWishlisted ? t('removeFromWishlist', { defaultValue: 'Remove from wishlist' }) : t('addToWishlist', { defaultValue: 'Add to wishlist' })}
          >
            <Heart size={18} fill={isWishlisted ? '#ef4444' : 'none'} />
          </button>

          {!imageLoaded && <div className="image-skeleton" aria-hidden="true" />}
          <img
            src={image}
            alt={name}
            className={`product-image ${imageLoaded ? 'loaded' : ''}`}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
            decoding="async"
          />

          <div className={`product-overlay ${isHovered ? 'visible' : ''}`}>
            <div className="overlay-actions">
              <button 
                className={`action-btn add-cart-btn ${addedToCart ? 'added' : ''}`}
                onClick={handleAddToCart}
                disabled={isAddingToCart || !inStock}
                title={t('addToCart')}
              >
                {addedToCart ? (
                  <>
                    <Check size={18} />
                    <span>{t('added', { defaultValue: 'Added!' })}</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart size={18} />
                    <span>{t('addToCart')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="product-info">
          {/* Color Options Preview */}
          {colors && colors.length > 0 && (
            <div className="color-options">
              {colors.slice(0, 4).map((color, idx) => (
                <span 
                  key={idx} 
                  className="color-dot" 
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
              {colors.length > 4 && (
                <span className="color-more">+{colors.length - 4}</span>
              )}
            </div>
          )}

          <h3 className="product-name">{name}</h3>

          <div className="product-prices">
            <span className="current-price">
              {fromPrice ? `${t('fromPrice', { price: formatPrice(newPrice) })}` : formatPrice(newPrice)}
            </span>
            {oldPrice && oldPrice > newPrice && (
              <span className="original-price">{formatPrice(oldPrice)}</span>
            )}
          </div>

          <div className="product-meta">
            <div className="product-rating">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={12}
                    fill={i < Math.floor(rating) ? '#fbbf24' : 'none'}
                    stroke={i < Math.floor(rating) ? '#fbbf24' : '#d1d5db'}
                  />
                ))}
              </div>
              <span className="rating-value">{rating.toFixed(1)}</span>
              <span className="sold-count">({sold} {t('sold', { defaultValue: 'sold' })})</span>
            </div>
          </div>

          <div className="product-shipping">
            <Truck size={13} />
            <span>{shipping}</span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default Item;
